import { useEffect, useMemo, useRef } from 'react';
import { useEditor } from '../../state/editorStore';
import { WordLookEditor } from './WordLookControls';
import { WordAnimationEditor } from './WordAnimationEditor';
import { playbackClock } from '../../playback/clock';
import { resolveWordLook, rowStyleAt } from '@captionflow/caption-engine';

/**
 * Right floating panel - style the selected word, or ALL words when several are
 * multi-selected on the timeline (shift / ⌘-click). Every control writes the
 * real word model (emphasis, size, bold, colour, hide), updates the preview
 * live, is undoable, and - in multi mode - overwrites every selected word.
 */
export function StudioInspector() {
  const words = useEditor((s) => s.words);
  const selectedWordId = useEditor((s) => s.selectedWordId);
  const selectedWordIds = useEditor((s) => s.selectedWordIds);
  const selectWord = useEditor((s) => s.selectWord);
  const clearWordSelection = useEditor((s) => s.clearWordSelection);
  const editWordText = useEditor((s) => s.editWordText);
  const styleWords = useEditor((s) => s.styleWords);
  useEditor((s) => s.styleOverrides);
  useEditor((s) => s.templateId);
  useEditor((s) => s.segments);
  const resetWordsStyle = useEditor((s) => s.resetWordsStyle);

  const byId = useMemo(() => new Map(words.map((w) => [w.id, w])), [words]);
  const replayStop = useRef<(() => void) | null>(null);
  useEffect(
    () => () => {
      if (replayStop.current) {
        replayStop.current();
        playbackClock.pause();
      }
    },
    [selectedWordId, selectedWordIds],
  );
  const multi = selectedWordIds.length > 1;
  // Which words the controls act on, and the "primary" whose state is displayed.
  const targetIds = multi ? selectedWordIds : selectedWordId ? [selectedWordId] : [];
  const primary =
    (selectedWordId && targetIds.includes(selectedWordId) && byId.get(selectedWordId)) ||
    byId.get(targetIds[0] ?? '') ||
    null;

  // Nothing selected → hide the panel entirely (the stage stays centred because
  // the grid still reserves the right gutter column).
  if (!primary || targetIds.length === 0) return null;

  const size = primary.sizeScale ?? 1;
  const anyCustom = targetIds.some((id) => {
    const w = byId.get(id);
    return w && (w.colorOverride || w.sizeScale || w.bold || w.wordStyle);
  });
  const apply = (patch: Parameters<typeof styleWords>[1]) => styleWords(targetIds, patch);
  const ws = primary.wordStyle ?? {};
  const state = useEditor.getState();
  const segment = state.segments.find((seg) => seg.wordIds.includes(primary.id));
  const style = segment ? state.styleForSegment(segment) : state.resolvedStyle();
  const rowIndex =
    segment?.wordIds
      .slice(0, segment.wordIds.indexOf(primary.id))
      .reduce((count, id) => count + (byId.get(id)?.wordStyle?.breakAfter ? 1 : 0), 0) ?? 0;
  const look = resolveWordLook(style, primary, rowStyleAt(style, rowIndex));
  const replay = () => {
    replayStop.current?.();
    playbackClock.pause();
    playbackClock.seek(Math.max(0, primary.startMs - 80));
    const end = Math.min(
      state.durationMs || Infinity,
      Math.max(primary.endMs, segment?.endMs ?? primary.endMs) + 100,
    );
    replayStop.current = playbackClock.subscribe((time) => {
      if (time >= end) {
        replayStop.current?.();
        replayStop.current = null;
        playbackClock.pause();
      }
    });
    playbackClock.play();
  };
  const setWs = (p: NonNullable<Parameters<typeof styleWords>[1]['wordStyle']>) =>
    apply({ wordStyle: p });

  return (
    <aside className="studio-right">
      <div className="si-head">
        {multi ? (
          <>
            <span className="si-kicker">SELECTION</span>
            <div className="si-multi">
              <strong>{selectedWordIds.length} words selected</strong>
              <button className="si-x" onClick={clearWordSelection} aria-label="Clear selection">
                ✕
              </button>
            </div>
            <p className="si-multi-hint">Changes apply to all selected words.</p>
          </>
        ) : (
          <>
            <span className="si-kicker">EDIT WORD</span>
            <div className="si-word-row">
              <input
                className="si-word"
                defaultValue={primary.text}
                key={primary.id}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                }}
                onBlur={(e) =>
                  e.target.value.trim() && editWordText(primary.id, e.target.value.trim())
                }
              />
              <button className="si-x" onClick={() => selectWord(null)} aria-label="Deselect">
                ✕
              </button>
            </div>
          </>
        )}
      </div>

      <p className="si-scope">
        {anyCustom ? 'Custom word styling' : 'Using caption style'}
        {multi ? ' · Preview shows one word' : ''}
      </p>
      <div className="shk-fields si-body si-scroll">
        <section className="si-flow-section">
          <h3>Edit word</h3>
          <Row label="Emphasize">
            <Toggle
              on={!!primary.emphasized}
              onClick={() => apply({ emphasized: !primary.emphasized })}
            />
          </Row>
          <Row label="Size">
            <div className="shk-size">
              <input
                type="range"
                min={0.6}
                max={2}
                step={0.05}
                value={size}
                onChange={(e) => apply({ sizeScale: Number(e.target.value) })}
              />
              <span>{Math.round(size * 100)}%</span>
            </div>
          </Row>

          <div className="ses-divider" />

          <Row label="Word color">
            <input
              type="color"
              className="shk-color"
              value={hex(primary.colorOverride, '#ffffff')}
              onChange={(e) => apply({ colorOverride: e.target.value })}
            />
          </Row>
          {primary.colorOverride && (
            <button className="si-linkbtn" onClick={() => apply({ colorOverride: null })}>
              Reset to template color
            </button>
          )}
        </section>
        <section className="si-flow-section">
          <h3>Style details</h3>
          <WordLookEditor
            value={ws}
            onChange={setWs}
            showPill
            grouped
            inheritFont={primary.emphasized ? 'Emphasis font' : 'Caption font'}
            inheritFamily={look.family}
            inheritWeight={look.weight}
            typographyCustom={!!primary.bold}
            typographyExtra={
              <Row label="Bold">
                <Toggle on={!!primary.bold} onClick={() => apply({ bold: !primary.bold })} />
              </Row>
            }
          />
          <details className="si-group">
            <summary>
              Layout & visibility
              {(ws.breakAfter || primary.hidden) && <span className="si-custom">Custom</span>}
            </summary>
            <div className="si-group-body">
              <Row label="Line break after">
                <Toggle
                  on={!!ws.breakAfter}
                  onClick={() => setWs({ breakAfter: ws.breakAfter ? null : true })}
                />
              </Row>
              <Row label="Hide from captions">
                <Toggle on={!!primary.hidden} onClick={() => apply({ hidden: !primary.hidden })} />
              </Row>
            </div>
          </details>
        </section>
        <details className="si-group si-flow-section si-animate">
          <summary>Animate{ws.motion != null && <span className="si-custom">Custom</span>}</summary>
          <div className="si-group-body">
            <WordAnimationEditor
              key={primary.id}
              value={ws.motion}
              inherited={primary.emphasized ? (style.emphasis.motion ?? undefined) : undefined}
              captionEntrance={
                style.revealMode === 'word'
                  ? (style.wordEntrance ?? style.activeWord.animation)
                  : style.entrance
              }
              text={primary.text}
              fontFamily={look.family}
              fontWeight={look.weight}
              onChange={(motion) => setWs({ motion })}
              onReplay={replay}
            />
          </div>
        </details>
      </div>

      <div className="si-foot">
        <button
          className="si-reset"
          disabled={!anyCustom}
          onClick={() => resetWordsStyle(targetIds)}
        >
          Reset {multi ? 'styling on all' : 'word styling'}
        </button>
      </div>
    </aside>
  );
}

function hex(value: string | undefined, fallback: string): string {
  if (value && /^#[0-9a-fA-F]{6}$/.test(value)) return value;
  return fallback;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="shk-row">
      <span>{label}</span>
      {children}
    </div>
  );
}

function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button className={on ? 'shk-toggle on' : 'shk-toggle'} onClick={onClick} aria-pressed={on}>
      <span className="shk-toggle-knob" />
    </button>
  );
}
